import { Form, Link, useActionData, useLoaderData, useNavigation, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { Candidate, OnboardingRun } from "@edm/contracts";
import { Lines, Loading, SettingsRowsSkeleton } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { translate, useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Callout, FormError, PageHeader, SettingsBlock, SettingsRow, SettingsSection, Skeleton, StatusBadge } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

// Repository onboarding (ENG-11): what the latest snapshot found, and the
// review queue. Nothing here becomes organizational truth until a person
// confirms it; confirming a repository record keeps its id and stated status.

export function onboardingLoader({ context }: LoaderFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const onboarding = Promise.all([
    api.onboardingRuns(),
    api.candidates({ kind: "decision_candidate", status: "suggested" }),
    api.candidates({ kind: "constraint_candidate", status: "suggested" }),
    api.candidates({ kind: "component_candidate", status: "suggested" }),
  ]).then(([runs, decisions, constraints, components]) => ({ runs: runs.items, decisions: decisions.items, constraints: constraints.items, components: components.items }));
  return { onboarding: deferred(onboarding) };
}

export async function onboardingAction({ context, request }: ActionFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const form = await request.formData();
  const id = String(form.get("id") ?? "");
  try {
    if (form.get("intent") === "reject") {
      await api.rejectCandidate(id, String(form.get("reason") ?? "") || undefined);
      return { ok: translate("onboarding.rejected") };
    }
    const res = await api.confirmCandidate(id, form.get("activate") === "true");
    return {
      ok: translate("onboarding.created", { id: res.decisionId ?? res.constraintId ?? res.componentId ?? "" }),
      decisionId: res.decisionId,
      componentId: res.componentId,
    };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

type DecisionPayload = { source: string; title: string; status?: string; decisionId?: string; path?: string; outcome?: string; rationale?: string; citations?: { evidenceId: string; quote: string }[]; idConflict?: boolean };
type ConstraintPayload = { statement: string; category: string; path: string; line: number };
type ComponentPayload = { name: string; kind: string; keys: string[]; patterns: string[]; source: string; repository: string; manifests?: string[] };

function RunSummary({ run }: { run: OnboardingRun }) {
  const { t, tOr, formatDateTime } = useI18n();
  const s = run.summary;
  return (
    <SettingsSection title={t("onboarding.run.title")} description={run.repository}>
      {[
          { key: t("onboarding.run.status"), value: <StatusBadge status={run.status === "succeeded" ? "accepted" : run.status === "failed" ? "rejected" : "proposed"} /> },
          { key: t("onboarding.run.revision"), value: <code>{s?.revision ?? t("common.none")}</code> },
          { key: t("onboarding.run.finished"), value: run.finishedAt ? formatDateTime(run.finishedAt) : t("common.none") },
          ...(s
            ? [
                {
                  key: t("onboarding.run.documents"),
                  value:
                    Object.entries(s.documents)
                      .map(([kind, count]) => t("onboarding.run.documentsItem", { count, kind: tOr(`documentKind.${kind}`, kind.replaceAll("_", " ")) }))
                      .join(", ") || t("onboarding.run.documentsNone"),
                },
                { key: t("onboarding.run.history"), value: t("onboarding.run.historyValue", { commits: s.commits ?? 0, changes: s.changes ?? 0 }) },
                {
                  key: t("onboarding.run.candidates"),
                  value: t("onboarding.run.candidatesValue", {
                    decisions: s.decisionCandidates ?? 0,
                    constraints: s.constraintCandidates ?? 0,
                    components: s.componentCandidates ?? 0,
                    inMemory: s.alreadyInMemory ?? 0,
                    suppressed: s.suppressed ?? 0,
                  }),
                },
                {
                  key: t("onboarding.run.inference"),
                  value: (
                    <span>
                      <Badge tone={s.inference.status === "succeeded" ? "success" : s.inference.status === "failed" ? "danger" : "neutral"}>
                        {tOr(`status.${s.inference.status}`, s.inference.status.replaceAll("_", " "))}
                      </Badge>
                      {s.inference.status === "succeeded" && t("onboarding.run.inferenceValue", { kept: s.inference.kept ?? 0, dropped: s.inference.dropped ?? 0 })}
                    </span>
                  ),
                },
              ]
            : []),
          ...(run.error ? [{ key: t("onboarding.run.error"), value: run.error }] : []),
      ].map((it) => (
        <SettingsRow key={it.key} label={it.key}>
          {it.value}
        </SettingsRow>
      ))}
      {(s?.unresolved?.length ?? 0) > 0 && (
        <SettingsBlock>
          <Callout tone="warning" title={t("onboarding.run.unresolved")}>
            {s!.unresolved!.map((u) => (
              <div key={u.path}>
                <code>{u.path}</code>: {u.reason}
              </div>
            ))}
          </Callout>
        </SettingsBlock>
      )}
    </SettingsSection>
  );
}

function Review({ id, busy, activate }: { id: string; busy: boolean; activate?: boolean }) {
  const { t } = useI18n();
  return (
    <span className="dt-inline dt-gap-2">
      <Form method="post">
        <input type="hidden" name="intent" value="reject" />
        <input type="hidden" name="id" value={id} />
        <Button type="submit" size="sm" variant="ghost" loading={busy}>
          {t("onboarding.reject")}
        </Button>
      </Form>
      <Form method="post">
        <input type="hidden" name="intent" value="confirm" />
        <input type="hidden" name="id" value={id} />
        {activate && <input type="hidden" name="activate" value="true" />}
        <Button type="submit" size="sm" variant="primary" loading={busy}>
          {activate ? t("onboarding.confirmAndActivate") : t("onboarding.confirm")}
        </Button>
      </Form>
    </span>
  );
}

/** The latest run's facts, then the candidate queues with their buttons. */
function CandidatesSkeleton() {
  const { t } = useI18n();
  const review = (
    <span className="dt-inline dt-gap-2">
      <Skeleton shape="button-sm" width={64} />
      <Skeleton shape="button-sm" width={80} />
    </span>
  );
  return (
    <Loading>
      <SettingsSection title={t("onboarding.run.title")} description={<Skeleton width={240} />}>
        <SettingsRow label={t("onboarding.run.status")}>
          <Skeleton shape="badge" width={72} />
        </SettingsRow>
        <SettingsRow label={t("onboarding.run.revision")}>
          <Skeleton width={96} />
        </SettingsRow>
        <SettingsRow label={t("onboarding.run.finished")}>
          <Skeleton width={140} />
        </SettingsRow>
      </SettingsSection>
      <SettingsSection title={<Skeleton width={200} />}>
        <SettingsRowsSkeleton count={2} control={review} />
      </SettingsSection>
      <SettingsSection title={<Skeleton width={220} />}>
        <SettingsRowsSkeleton count={2} control={review} />
      </SettingsSection>
    </Loading>
  );
}

export function OnboardingSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("settings.repositories.title")} description={t("onboarding.description")} />
      <SettingsSection title={t("onboarding.connect.title")}>
        <SettingsBlock>
          <Lines count={3} />
        </SettingsBlock>
      </SettingsSection>
      <CandidatesSkeleton />
    </>
  );
}

export function OnboardingRoute() {
  const { onboarding } = useLoaderData<typeof onboardingLoader>();
  const result = useActionData() as { ok?: string; error?: string; decisionId?: string; componentId?: string } | undefined;
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const base = `/w/${workspace.slug}`;
  return (
    <>
      <PageHeader title={t("settings.repositories.title")} description={t("onboarding.description")} />
      <FormError message={result?.error} />
      {result?.ok && (
        <Callout tone="success">
          {result.ok} {result.decisionId && <Link to={`${base}/decisions/${result.decisionId}`}>{t("onboarding.open", { id: result.decisionId })}</Link>}
          {result.componentId && <Link to={`${base}/components/${result.componentId}`}>{t("onboarding.open", { id: result.componentId })}</Link>}
        </Callout>
      )}
      <SettingsSection title={t("onboarding.connect.title")}>
        <SettingsBlock>
          <p className="dt-muted">{t("onboarding.connect.intro")}</p>
          <pre className="dt-mono" style={{ whiteSpace: "pre-wrap" }}>
            {`DIOTIA_API_KEY=… npx diotia push --workspace ${workspace.id} --api-url ${typeof window === "undefined" ? "" : window.location.origin}`}
          </pre>
          <p className="dt-subtle">{t("onboarding.connect.infer")}</p>
        </SettingsBlock>
      </SettingsSection>
      <Deferred data={onboarding} fallback={<CandidatesSkeleton />}>
        {(data) => <Candidates {...data} />}
      </Deferred>
    </>
  );
}

function Candidates({ runs, decisions, constraints, components }: { runs: OnboardingRun[]; decisions: Candidate[]; constraints: Candidate[]; components: Candidate[] }) {
  const busy = useNavigation().state === "submitting";
  const { t, tOr } = useI18n();
  const latest = runs.at(-1);
  return (
    <>
      {latest ? (
        <RunSummary run={latest} />
      ) : (
        <SettingsSection title={t("onboarding.run.title")}>
          <SettingsBlock>
            <EmptyState title={t("onboarding.empty.title")} hint={t("onboarding.empty.hint")} />
          </SettingsBlock>
        </SettingsSection>
      )}

      <SettingsSection title={t("onboarding.decisionCandidates", { count: decisions.length })}>
        {decisions.length === 0 ? (
          <SettingsBlock>
            <p className="dt-subtle">{t("common.nothingToReview")}</p>
          </SettingsBlock>
        ) : (
          decisions.map((c) => {
            const p = c.payload as DecisionPayload;
            return (
              <SettingsRow
                key={c.id}
                data-testid="decision-candidate"
                label={
                  <span className="dt-inline dt-gap-2 dt-align-center">
                    {p.title}
                    <Badge tone={p.source === "inference" ? "info" : "neutral"}>
                      {p.source === "inference" ? t("onboarding.source.inferred", { confidence: Math.round(c.confidence * 100) }) : t("onboarding.source.repositoryRecord")}
                    </Badge>
                    {p.status && <StatusBadge status={p.status} />}
                    {p.decisionId && <code>{p.decisionId}</code>}
                  </span>
                }
                description={
                  <>
                    {p.path && <div className="dt-mono">{p.path}</div>}
                    {p.outcome && <div>{p.outcome}</div>}
                    {p.idConflict && <div>{t("onboarding.idConflict")}</div>}
                    {p.citations?.map((q) => (
                      <div key={q.evidenceId + q.quote} className="dt-subtle">
                        “{q.quote}” — {q.evidenceId}
                      </div>
                    ))}
                  </>
                }
              >
                <Review id={c.id} busy={busy} />
              </SettingsRow>
            );
          })
        )}
      </SettingsSection>

      <SettingsSection title={t("onboarding.constraintCandidates", { count: constraints.length })} description={t("onboarding.constraintCandidates.description")}>
        {constraints.length === 0 ? (
          <SettingsBlock>
            <p className="dt-subtle">{t("common.nothingToReview")}</p>
          </SettingsBlock>
        ) : (
          constraints.map((c) => {
            const p = c.payload as ConstraintPayload;
            return (
              <SettingsRow
                key={c.id}
                label={p.statement}
                description={
                  <span className="dt-inline dt-gap-2 dt-align-center">
                    <Badge tone="neutral">{tOr(`constraintCategory.${p.category}`, p.category.replaceAll("_", " "))}</Badge>
                    <span className="dt-mono">
                      {p.path}:{p.line}
                    </span>
                  </span>
                }
              >
                <Review id={c.id} busy={busy} activate />
              </SettingsRow>
            );
          })
        )}
      </SettingsSection>

      <SettingsSection title={t("onboarding.componentCandidates", { count: components.length })} description={t("onboarding.componentCandidates.description")}>
        {components.length === 0 ? (
          <SettingsBlock>
            <p className="dt-subtle">{t("common.nothingToReview")}</p>
          </SettingsBlock>
        ) : (
          components.map((c) => {
            const p = c.payload as ComponentPayload;
            return (
              <SettingsRow
                key={c.id}
                data-testid="component-candidate"
                label={
                  <span className="dt-inline dt-gap-2 dt-align-center">
                    {p.name}
                    <Badge tone="neutral">{tOr(`componentKind.${p.kind}`, p.kind)}</Badge>
                    <Badge tone={p.source === "declared" ? "success" : "info"}>
                      {p.source === "declared" ? t("onboarding.source.declared") : t("onboarding.source.manifest", { confidence: Math.round(c.confidence * 100) })}
                    </Badge>
                  </span>
                }
                description={
                  <>
                    <div className="dt-mono">{p.keys.join(", ")}</div>
                    <div className="dt-mono">{p.patterns.join(", ")}</div>
                  </>
                }
              >
                <Review id={c.id} busy={busy} />
              </SettingsRow>
            );
          })
        )}
      </SettingsSection>
    </>
  );
}
